// Durable delivery of activity events. Business actions never wait for the
// log: an event is stored in this browser first and sent in the background,
// again after a reload or reconnect. The event id is fixed when the event is
// created and the log only accepts new documents, so a resend never stores a
// second copy.
//
// One storage key per event: tabs of the same browser add and remove their
// own keys and never rewrite a shared list, so no tab loses another's events.
// If the browser storage fails, events are kept for the life of the page.
//
// The queue is bounded (count and age) and belongs to the account that
// created the events: another login on the same device never sends them,
// and they are discarded as soon as a different account signs in.
import { ActivityEvent } from './events';

export type Transport = (event: ActivityEvent) => Promise<void>;

export type DeliveryStatus = {
  /** Stored here, not yet confirmed by the server. */
  pending: number;
  /** Pending longer than the delay threshold (offline, slow, retrying). */
  delayed: number;
  /** Kept only in this page because the browser storage failed. */
  volatile: number;
  /** Refused by the server on the first attempt and discarded (this page). */
  rejected: number;
  /**
   * Refused on a resend: possibly stored by an earlier unanswered attempt,
   * possibly never stored. Not retried, not claimed as delivered (this page).
   */
  unconfirmed: number;
  /** Discarded because the queue was full or too old (this page). */
  dropped: number;
};

type Entry = { event: ActivityEvent; queuedAt: number; attempts: number; sendingUntil?: number };

type Options = {
  storage: Storage | null;
  send: Transport;
  now?: () => number;
  prefix?: string;
  maxEntries?: number;
  maxAgeMs?: number;
  delayAfterMs?: number;
  /** How long another tab leaves an event alone while this one sends it. */
  sendingLeaseMs?: number;
};

const DAY = 24 * 60 * 60 * 1000;

/** A refusal that a retry cannot change; anything else is retried later. */
const isRefusal = (error: unknown) =>
  ['permission-denied', 'invalid-argument', 'failed-precondition', 'already-exists'].includes(
    (error as any)?.code
  );

export const createOutbox = ({
  storage,
  send,
  now = Date.now,
  prefix = 'securityActivity.event.',
  maxEntries = 200,
  maxAgeMs = 7 * DAY,
  delayAfterMs = 30 * 1000,
  sendingLeaseMs = 60 * 1000,
}: Options) => {
  const memory = new Map<string, Entry>();
  const inFlight = new Set<string>();
  const listeners = new Set<() => void>();
  const counts = { rejected: 0, unconfirmed: 0, dropped: 0 };

  const notify = () => listeners.forEach((listener) => listener());

  const storedIds = (): string[] => {
    try {
      const ids: string[] = [];
      for (let i = 0; storage && i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) ids.push(key.slice(prefix.length));
      }
      return ids;
    } catch {
      return [];
    }
  };

  /** The page's own copy first: it is newer whenever the storage refused a write. */
  const read = (id: string): Entry | null => {
    const kept = memory.get(id);
    if (kept) return kept;
    try {
      const entry = JSON.parse(storage?.getItem(prefix + id) ?? 'null');
      if (entry?.event?.eventId === id) return entry;
    } catch {
      // A broken entry is not an event.
    }
    return null;
  };

  const all = (): Entry[] => {
    const ids = new Set([...storedIds(), ...memory.keys()]);
    return Array.from(ids)
      .map(read)
      .filter((e): e is Entry => !!e);
  };

  const write = (entry: Entry) => {
    try {
      if (!storage) throw new Error('no storage');
      storage.setItem(prefix + entry.event.eventId, JSON.stringify(entry));
      memory.delete(entry.event.eventId);
    } catch {
      memory.set(entry.event.eventId, entry);
    }
  };

  const remove = (id: string) => {
    memory.delete(id);
    try {
      storage?.removeItem(prefix + id);
    } catch {
      // Removal is retried with the next prune; a resend is refused, not stored twice.
    }
  };

  /** Drops too old entries, then the oldest beyond the limit; returns the rest. */
  const prune = () => {
    const entries = all().sort((a, b) => a.queuedAt - b.queuedAt);
    const kept = entries.filter((e) => now() - e.queuedAt <= maxAgeMs);
    const overflow = Math.max(0, kept.length - maxEntries);
    for (const e of [...entries.filter((e) => !kept.includes(e)), ...kept.slice(0, overflow)]) {
      remove(e.event.eventId);
      counts.dropped += 1;
    }
    return kept.slice(overflow);
  };

  const deliver = async (entry: Entry) => {
    const { eventId } = entry.event;
    inFlight.add(eventId);
    // Counted before sending: after a reload the next attempt knows an
    // earlier one may already have reached the server.
    write({ ...entry, attempts: entry.attempts + 1, sendingUntil: now() + sendingLeaseMs });
    try {
      await send(entry.event);
      remove(eventId);
    } catch (error) {
      if (isRefusal(error)) {
        if (entry.attempts === 0) counts.rejected += 1;
        else counts.unconfirmed += 1;
        remove(eventId);
      } else {
        write({ ...entry, attempts: entry.attempts + 1 });
      }
    } finally {
      inFlight.delete(eventId);
      notify();
    }
  };

  return {
    add(event: ActivityEvent) {
      write({ event, queuedAt: now(), attempts: 0 });
      prune();
      notify();
    },

    /** Sends the events of this account that no tab is sending right now. */
    flush(accountUid: string) {
      const waiting = prune().filter(
        (e) =>
          e.event.accountUid === accountUid &&
          !inFlight.has(e.event.eventId) &&
          !(e.sendingUntil && e.sendingUntil > now())
      );
      return Promise.all(waiting.map(deliver)).then(() => undefined);
    },

    forgetOtherAccounts(accountUid: string) {
      const foreign = all().filter((e) => e.event.accountUid !== accountUid);
      foreign.forEach((e) => remove(e.event.eventId));
      if (foreign.length) notify();
    },

    status(accountUid: string): DeliveryStatus {
      const own = all().filter((e) => e.event.accountUid === accountUid);
      return {
        pending: own.length,
        delayed: own.filter((e) => now() - e.queuedAt > delayAfterMs).length,
        volatile: own.filter((e) => memory.has(e.event.eventId)).length,
        ...counts,
      };
    },

    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type Outbox = ReturnType<typeof createOutbox>;
